import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from '@/components/ui/toaster'

// Layouts
import SiteOfficielLayout from '@/layouts/SiteOfficielLayout'
import EspaceProContentLayout from '@/layouts/EspaceProContentLayout'
import EvenementsLayout from '@/layouts/EvenementsLayout'
import FlashInfosLayout from '@/layouts/FlashInfosLayout'

// ===== Site Officiel Pages =====
const AccueilPage = lazy(() => import('@/app/(site-officiel)/accueil/page'))
const NaturePage = lazy(() => import('@/app/(site-officiel)/nature/page'))
const SoleilPlagePage = lazy(() => import('@/app/(site-officiel)/soleil-plage/page'))
const AffairesPage = lazy(() => import('@/app/(site-officiel)/affaires/page'))
const ArtDeVivreGastroPage = lazy(() => import('@/app/(site-officiel)/art-de-vivre-gastro/page'))
const CulturePatrimoineArtPage = lazy(() => import('@/app/(site-officiel)/culture-patrimoine-art/page'))
const AgendaPage = lazy(() => import('@/app/(site-officiel)/agenda/page'))

// Découvertes
const NosInspirationsVoyagesPage = lazy(() => import('@/app/(site-officiel)/decouvertes/nos-inspirations-voyages/page'))
const NosRegionsPage = lazy(() => import('@/app/(site-officiel)/decouvertes/nos-regions/page'))
const RegionCentrePage = lazy(() => import('@/app/(site-officiel)/decouvertes/nos-regions/centre/page'))
const RegionNordPage = lazy(() => import('@/app/(site-officiel)/decouvertes/nos-regions/nord/page'))
const RegionOuestPage = lazy(() => import('@/app/(site-officiel)/decouvertes/nos-regions/ouest/page'))
const RegionSudPage = lazy(() => import('@/app/(site-officiel)/decouvertes/nos-regions/sud/page'))
const RegionSudOuestPage = lazy(() => import('@/app/(site-officiel)/decouvertes/nos-regions/sud-ouest/page'))
const NotreHistoirePage = lazy(() => import('@/app/(site-officiel)/decouvertes/notre-histoire/page'))
const NotreHospitaliteAkwabaPage = lazy(() => import('@/app/(site-officiel)/decouvertes/notre-hospitalite-akwaba/page'))
const NotreLangageDeRuePage = lazy(() => import('@/app/(site-officiel)/decouvertes/notre-langage-de-rue/page'))

// Guide de voyage
const GuideDeVoyagePage = lazy(() => import('@/app/(site-officiel)/guide-de-voyage/guide-de-voyage/page'))
const InfosUtilesPage = lazy(() => import('@/app/(site-officiel)/guide-de-voyage/infos-utiles/page'))

// IvoirExperience
const GoutDivoirePage = lazy(() => import('@/app/(site-officiel)/ivoirexperience/gout-divoire/page'))

// Actualités - Foires et salons
const FoiresEtSalonsPage = lazy(() => import('@/app/(site-officiel)/actualites/foires-et-salons/page'))
const IftmPage = lazy(() => import('@/app/(site-officiel)/actualites/foires-et-salons/iftm/page'))

// Actualités - Événements (wrapped by EvenementsLayout)
const EvenementsPage = lazy(() => import('@/app/(site-officiel)/actualites/evenements/page'))
const AfricaFotoFairPage = lazy(() => import('@/app/(site-officiel)/actualites/evenements/2025/11/africa-foto-fair/page'))
const AbissaChasseTresorPage = lazy(() => import('@/app/(site-officiel)/actualites/evenements/2025/11/abissa-chasse-tresor-grand-bassam/page'))

// Actualités - Flash Infos (wrapped by FlashInfosLayout)
const FlashInfosPage = lazy(() => import('@/app/(site-officiel)/actualites/flash-infos/page'))
const WcfParcNationalTaiPage = lazy(() => import('@/app/(site-officiel)/actualites/flash-infos/2025/11/wcf-parc-national-tai/page'))
const InterventionMalekaSanPedroPage = lazy(() => import('@/app/(site-officiel)/actualites/flash-infos/2025/11/intervention-maleka-san-pedro/page'))
const MasterclassMalekahPage = lazy(() => import('@/app/(site-officiel)/actualites/flash-infos/2025/11/masterclass-malekah/page'))
const JacquevilleMonteeEauxPage = lazy(() => import('@/app/(site-officiel)/actualites/flash-infos/jacqueville-monntee-eaux/page'))
const HautConseilTransporteursPage = lazy(() => import('@/app/(site-officiel)/actualites/flash-infos/haut-conseil-des-transporteurs/page'))
const ChoiseulAfricaPage = lazy(() => import('@/app/(site-officiel)/actualites/flash-infos/choiseul-africa/page'))
const SiabPage = lazy(() => import('@/app/(site-officiel)/actualites/flash-infos/siab/page'))
const InitiativeCacaoAnacPage = lazy(() => import('@/app/(site-officiel)/actualites/flash-infos/initiative-cacao-anac/page'))
const InitiativeCacaoPage = lazy(() => import('@/app/(site-officiel)/actualites/flash-infos/initiative-cacao/page'))

// ===== Espace Pro Pages =====
const EspaceProHomePage = lazy(() => import('@/app/espace-pro/page'))
const OfficeDeTourismePage = lazy(() => import('@/app/espace-pro/(content)/office-de-tourisme/page'))
const QuiSommesNousPage = lazy(() => import('@/app/espace-pro/(content)/office-de-tourisme/qui-sommes-nous/page'))
const BudgetFinancesPage = lazy(() => import('@/app/espace-pro/(content)/office-de-tourisme/qui-sommes-nous/budget-finances-et-moyens-generaux/page'))
const DirecteurGeneralPage = lazy(() => import('@/app/espace-pro/(content)/office-de-tourisme/qui-sommes-nous/directeur-general-et-ordonnateur/page'))
const MarketingCommunicationPage = lazy(() => import('@/app/espace-pro/(content)/office-de-tourisme/qui-sommes-nous/marketing-communication/page'))
const RelationsExterieuresPage = lazy(() => import('@/app/espace-pro/(content)/office-de-tourisme/qui-sommes-nous/relations-exterieures/page'))
const RessourcesHumainesPage = lazy(() => import('@/app/espace-pro/(content)/office-de-tourisme/qui-sommes-nous/ressources-humaines-formation-et-perfectionnement/page'))
const StatistiquesSuiviPage = lazy(() => import('@/app/espace-pro/(content)/office-de-tourisme/qui-sommes-nous/statistiques-et-suivi-evaluation/page'))
const PlansActionsPage = lazy(() => import('@/app/espace-pro/(content)/office-de-tourisme/plans-actions-rapports-activites/page'))
const RestezInformesPage = lazy(() => import('@/app/espace-pro/(content)/office-de-tourisme/restez-informes/page'))
const OutilDevTerritoiresPage = lazy(() => import('@/app/espace-pro/(content)/office-de-tourisme/notre-outil-de-developpement-des-territoires/page'))

const PoleObservationPage = lazy(() => import('@/app/espace-pro/(content)/pole-observation-etudes/page'))
const ObservatoireCivPage = lazy(() => import('@/app/espace-pro/(content)/pole-observation-etudes/observatoire-activite-toursistique-civ/page'))
const ObservatoireMondePage = lazy(() => import('@/app/espace-pro/(content)/pole-observation-etudes/observatoire-activite-toursistique-monde/page'))
const ReglementationPage = lazy(() => import('@/app/espace-pro/(content)/pole-observation-etudes/reglementation/page'))
const VeillePage = lazy(() => import('@/app/espace-pro/(content)/pole-observation-etudes/veille/page'))

const OfficeEtVousPage = lazy(() => import('@/app/espace-pro/(content)/office-et-vous/page'))
const CentreRessourcesPage = lazy(() => import('@/app/espace-pro/(content)/office-et-vous/centre-de-ressources/page'))
const DisponibilitePage = lazy(() => import('@/app/espace-pro/(content)/office-et-vous/centre-de-ressources/disponibilite/page'))
const RessourcesTourismeResponsablePage = lazy(() => import('@/app/espace-pro/(content)/office-et-vous/centre-de-ressources/ressources-tourisme-responsable/page'))
const VosOutilsAccueilPage = lazy(() => import('@/app/espace-pro/(content)/office-et-vous/centre-de-ressources/vos-outils-accueil/page'))
const EngagementChartePage = lazy(() => import('@/app/espace-pro/(content)/office-et-vous/engagement-charte/page'))
const QualifierMonOffrePage = lazy(() => import('@/app/espace-pro/(content)/office-et-vous/qualifier-mon-offre/page'))
const ActiviteMeubleTourismePage = lazy(() => import('@/app/espace-pro/(content)/office-et-vous/qualifier-mon-offre/activite-meuble-tourisme-chambre-hotes/page'))
const ClassementsMarquesLabelsPage = lazy(() => import('@/app/espace-pro/(content)/office-et-vous/qualifier-mon-offre/classements-marques-labels/page'))
const RendezVousProsPage = lazy(() => import('@/app/espace-pro/(content)/office-et-vous/rendez-vous-pros/page'))

const DevenirPartenairePage = lazy(() => import('@/app/espace-pro/(content)/devenir-partenaire/page'))
const GuideDuPartenairePage = lazy(() => import('@/app/espace-pro/(content)/devenir-partenaire/guide-du-partenaire/page'))
const OutilsBonneGouvernancePage = lazy(() => import('@/app/espace-pro/(content)/devenir-partenaire/outils-bonne-gouvernance/page'))
const PourquoiEtrePartenairePage = lazy(() => import('@/app/espace-pro/(content)/devenir-partenaire/pourquoi-etre-partenaire/page'))
const TourismeCapitalHumainPage = lazy(() => import('@/app/espace-pro/(content)/devenir-partenaire/tourisme-capital-humain/page'))

const PromotionPage = lazy(() => import('@/app/espace-pro/(content)/promotion/page'))
const ContactsProsPage = lazy(() => import('@/app/espace-pro/(content)/promotion/contacts-pros/page'))
const ForumsSeminairesPage = lazy(() => import('@/app/espace-pro/(content)/promotion/forums-seminaires/page'))

// ===== Other Pages =====
const PdfViewerPage = lazy(() => import('@/app/pdf-viewer/[filePath]/page'))
const NotFoundPage = lazy(() => import('@/app/not-found'))

function PageLoader() {
  return (
    <div className="flex justify-center items-center min-h-[400px]">
      <p className="text-xl">Chargement...</p>
    </div>
  )
}

export default function App() {
  return (
    <>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Redirect root to accueil */}
          <Route path="/" element={<Navigate to="/accueil" replace />} />

          {/* ===== Site Officiel (with header + footer layout) ===== */}
          <Route element={<SiteOfficielLayout />}>
            <Route path="/accueil" element={<AccueilPage />} />
            <Route path="/nature" element={<NaturePage />} />
            <Route path="/soleil-plage" element={<SoleilPlagePage />} />
            <Route path="/affaires" element={<AffairesPage />} />
            <Route path="/art-de-vivre-gastro" element={<ArtDeVivreGastroPage />} />
            <Route path="/culture-patrimoine-art" element={<CulturePatrimoineArtPage />} />
            <Route path="/agenda" element={<AgendaPage />} />

            {/* Découvertes */}
            <Route path="/decouvertes/nos-inspirations-voyages" element={<NosInspirationsVoyagesPage />} />
            <Route path="/decouvertes/nos-regions" element={<NosRegionsPage />} />
            <Route path="/decouvertes/nos-regions/centre" element={<RegionCentrePage />} />
            <Route path="/decouvertes/nos-regions/nord" element={<RegionNordPage />} />
            <Route path="/decouvertes/nos-regions/ouest" element={<RegionOuestPage />} />
            <Route path="/decouvertes/nos-regions/sud" element={<RegionSudPage />} />
            <Route path="/decouvertes/nos-regions/sud-ouest" element={<RegionSudOuestPage />} />
            <Route path="/decouvertes/notre-histoire" element={<NotreHistoirePage />} />
            <Route path="/decouvertes/notre-hospitalite-akwaba" element={<NotreHospitaliteAkwabaPage />} />
            <Route path="/decouvertes/notre-langage-de-rue" element={<NotreLangageDeRuePage />} />

            {/* Guide de voyage */}
            <Route path="/guide-de-voyage/guide-de-voyage" element={<GuideDeVoyagePage />} />
            <Route path="/guide-de-voyage/infos-utiles" element={<InfosUtilesPage />} />

            {/* IvoirExperience */}
            <Route path="/ivoirexperience/gout-divoire" element={<GoutDivoirePage />} />

            {/* Actualités - Foires et salons */}
            <Route path="/actualites/foires-et-salons" element={<FoiresEtSalonsPage />} />
            <Route path="/actualites/foires-et-salons/iftm" element={<IftmPage />} />

            {/* Actualités - Événements (with PostsProvider) */}
            <Route element={<EvenementsLayout />}>
              <Route path="/actualites/evenements" element={<EvenementsPage />} />
              <Route path="/actualites/evenements/2025/11/africa-foto-fair" element={<AfricaFotoFairPage />} />
              <Route path="/actualites/evenements/2025/11/abissa-chasse-tresor-grand-bassam" element={<AbissaChasseTresorPage />} />
            </Route>

            {/* Actualités - Flash Infos (with PostsProvider) */}
            <Route element={<FlashInfosLayout />}>
              <Route path="/actualites/flash-infos" element={<FlashInfosPage />} />
              <Route path="/actualites/flash-infos/2025/11/wcf-parc-national-tai" element={<WcfParcNationalTaiPage />} />
              <Route path="/actualites/flash-infos/2025/11/intervention-maleka-san-pedro" element={<InterventionMalekaSanPedroPage />} />
              <Route path="/actualites/flash-infos/2025/11/masterclass-malekah" element={<MasterclassMalekahPage />} />
              <Route path="/actualites/flash-infos/jacqueville-monntee-eaux" element={<JacquevilleMonteeEauxPage />} />
              <Route path="/actualites/flash-infos/haut-conseil-des-transporteurs" element={<HautConseilTransporteursPage />} />
              <Route path="/actualites/flash-infos/choiseul-africa" element={<ChoiseulAfricaPage />} />
              <Route path="/actualites/flash-infos/siab" element={<SiabPage />} />
              <Route path="/actualites/flash-infos/initiative-cacao-anac" element={<InitiativeCacaoAnacPage />} />
              <Route path="/actualites/flash-infos/initiative-cacao" element={<InitiativeCacaoPage />} />
            </Route>
          </Route>

          {/* ===== Espace Pro Home (own layout) ===== */}
          <Route path="/espace-pro" element={<EspaceProHomePage />} />

          {/* ===== Espace Pro Content (with GtLanguages + footer layout) ===== */}
          <Route element={<EspaceProContentLayout />}>
            {/* Office de Tourisme */}
            <Route path="/espace-pro/office-de-tourisme" element={<OfficeDeTourismePage />} />
            <Route path="/espace-pro/office-de-tourisme/qui-sommes-nous" element={<QuiSommesNousPage />} />
            <Route path="/espace-pro/office-de-tourisme/qui-sommes-nous/budget-finances-et-moyens-generaux" element={<BudgetFinancesPage />} />
            <Route path="/espace-pro/office-de-tourisme/qui-sommes-nous/directeur-general-et-ordonnateur" element={<DirecteurGeneralPage />} />
            <Route path="/espace-pro/office-de-tourisme/qui-sommes-nous/marketing-communication" element={<MarketingCommunicationPage />} />
            <Route path="/espace-pro/office-de-tourisme/qui-sommes-nous/relations-exterieures" element={<RelationsExterieuresPage />} />
            <Route path="/espace-pro/office-de-tourisme/qui-sommes-nous/ressources-humaines-formation-et-perfectionnement" element={<RessourcesHumainesPage />} />
            <Route path="/espace-pro/office-de-tourisme/qui-sommes-nous/statistiques-et-suivi-evaluation" element={<StatistiquesSuiviPage />} />
            <Route path="/espace-pro/office-de-tourisme/plans-actions-rapports-activites" element={<PlansActionsPage />} />
            <Route path="/espace-pro/office-de-tourisme/restez-informes" element={<RestezInformesPage />} />
            <Route path="/espace-pro/office-de-tourisme/notre-outil-de-developpement-des-territoires" element={<OutilDevTerritoiresPage />} />

            {/* Pôle Observation & Études */}
            <Route path="/espace-pro/pole-observation-etudes" element={<PoleObservationPage />} />
            <Route path="/espace-pro/pole-observation-etudes/observatoire-activite-toursistique-civ" element={<ObservatoireCivPage />} />
            <Route path="/espace-pro/pole-observation-etudes/observatoire-activite-toursistique-monde" element={<ObservatoireMondePage />} />
            <Route path="/espace-pro/pole-observation-etudes/reglementation" element={<ReglementationPage />} />
            <Route path="/espace-pro/pole-observation-etudes/veille" element={<VeillePage />} />

            {/* Office et Vous */}
            <Route path="/espace-pro/office-et-vous" element={<OfficeEtVousPage />} />
            <Route path="/espace-pro/office-et-vous/centre-de-ressources" element={<CentreRessourcesPage />} />
            <Route path="/espace-pro/office-et-vous/centre-de-ressources/disponibilite" element={<DisponibilitePage />} />
            <Route path="/espace-pro/office-et-vous/centre-de-ressources/ressources-tourisme-responsable" element={<RessourcesTourismeResponsablePage />} />
            <Route path="/espace-pro/office-et-vous/centre-de-ressources/vos-outils-accueil" element={<VosOutilsAccueilPage />} />
            <Route path="/espace-pro/office-et-vous/engagement-charte" element={<EngagementChartePage />} />
            <Route path="/espace-pro/office-et-vous/qualifier-mon-offre" element={<QualifierMonOffrePage />} />
            <Route path="/espace-pro/office-et-vous/qualifier-mon-offre/activite-meuble-tourisme-chambre-hotes" element={<ActiviteMeubleTourismePage />} />
            <Route path="/espace-pro/office-et-vous/qualifier-mon-offre/classements-marques-labels" element={<ClassementsMarquesLabelsPage />} />
            <Route path="/espace-pro/office-et-vous/rendez-vous-pros" element={<RendezVousProsPage />} />

            {/* Devenir Partenaire */}
            <Route path="/espace-pro/devenir-partenaire" element={<DevenirPartenairePage />} />
            <Route path="/espace-pro/devenir-partenaire/guide-du-partenaire" element={<GuideDuPartenairePage />} />
            <Route path="/espace-pro/devenir-partenaire/outils-bonne-gouvernance" element={<OutilsBonneGouvernancePage />} />
            <Route path="/espace-pro/devenir-partenaire/pourquoi-etre-partenaire" element={<PourquoiEtrePartenairePage />} />
            <Route path="/espace-pro/devenir-partenaire/tourisme-capital-humain" element={<TourismeCapitalHumainPage />} />

            {/* Promotion */}
            <Route path="/espace-pro/promotion" element={<PromotionPage />} />
            <Route path="/espace-pro/promotion/contacts-pros" element={<ContactsProsPage />} />
            <Route path="/espace-pro/promotion/forums-seminaires" element={<ForumsSeminairesPage />} />
          </Route>

          {/* ===== PDF Viewer ===== */}
          <Route path="/pdf-viewer/:filePath" element={<PdfViewerPage />} />

          {/* ===== 404 ===== */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
      <Toaster />
    </>
  )
}
